import { z } from 'zod';
import {
  isoDateSchema,
  longTextSchema,
  moneySchema,
  nonNegativeMoneySchema,
  positiveMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import { BIKE_OWNERSHIPS, PAYMENT_METHODS } from '../enums.js';

/**
 * Investors, who own bikes the company's riders ride, and the share of each
 * week's remittance that is theirs.
 *
 * Money collected for an investor is a payable — owed to them — and never
 * company revenue. Paying it out settles the payable; it is not an expense.
 */

export const investorInputSchema = z.object({
  name: shortTextSchema,
  /** The business's own reference for the investor ("Investor ID"). */
  reference: z.string().trim().min(1).max(60).nullish(),
  phone: z.string().trim().max(40).nullish(),
  email: z.string().trim().email().max(200).nullish(),
  address: longTextSchema.nullish(),
  note: longTextSchema.nullish(),
  isActive: z.boolean().optional(),
});
export type InvestorInput = z.infer<typeof investorInputSchema>;
export const investorUpdateSchema = investorInputSchema.partial();

export const investorBalancesSchema = z.object({
  /** Their share of every week charged on their bikes, paid or not (written off excluded). */
  entitled: moneySchema,
  /** The investor's share of remittance collected, all time. */
  collected: moneySchema,
  paidOut: moneySchema,
  /** Collected and not yet paid to them. */
  payable: moneySchema,
  /** Their share of weeks charged and not yet collected. */
  outstandingFromRiders: moneySchema,
  /** What their partnerships have earned them, week by week (0171): accrued earnings. */
  earningsDue: moneySchema,
  /** Earnings the agreement and records could not establish, waiting for the owner. */
  earningsInReview: moneySchema,
  earningsInReviewCount: z.number().int(),
  /** Earned and not yet paid out: earnings due less paid out. */
  outstandingBalance: moneySchema,
});

export const investorViewSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  reference: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  note: z.string().nullable(),
  isActive: z.boolean(),
  bikes: z.array(z.object({ id: uuidSchema, registration: z.string(), riderName: z.string().nullable() })),
  balances: investorBalancesSchema,
  lastPayoutOn: z.string().nullable(),
  createdAt: z.string(),
});
export type InvestorView = z.infer<typeof investorViewSchema>;

export const investorPayoutViewSchema = z.object({
  id: uuidSchema,
  investorId: uuidSchema,
  /** The vehicle partnership this payout settles. */
  investmentId: uuidSchema.nullable(),
  amount: moneySchema,
  paidOn: z.string(),
  method: z.string(),
  accountId: uuidSchema.nullable(),
  accountName: z.string().nullable(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
  voided: z.boolean(),
  voidReason: z.string().nullable(),
});
export type InvestorPayoutView = z.infer<typeof investorPayoutViewSchema>;

/** One week's collection credited to the investor. */
export const investorCollectionViewSchema = z.object({
  paymentId: uuidSchema,
  paidOn: z.string(),
  riderName: z.string(),
  bikeRegistration: z.string().nullable(),
  weekStartsOn: z.string(),
  weekEndsOn: z.string(),
  paymentAmount: moneySchema,
  investorAmount: moneySchema,
  companyAmount: moneySchema,
});

/**
 * One bike the investor owns or owned, for a stretch of dates. The link is the
 * ownership period: it starts on `effectiveFrom` and ends the day before the
 * bike's next ownership change (`effectiveTo`), or is still running.
 */
export const investorBikeLinkSchema = z.object({
  periodId: uuidSchema,
  bikeId: uuidSchema,
  registration: z.string(),
  bikeStatus: z.string(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  current: z.boolean(),
  /** Who holds the bike now (for a current link). */
  riderId: uuidSchema.nullable(),
  riderName: z.string().nullable(),
  riderSince: z.string().nullable(),
  /** The investor's weekly share in force today, and where it comes from. */
  weeklyShare: moneySchema,
  /** Set when the share is a percentage of the week's remittance instead of a fixed amount. */
  sharePercent: z.string().nullable(),
  shareSource: z.enum(['agreement', 'default']),
  /** The vehicle partnership this link belongs to. */
  investmentId: uuidSchema.nullable(),
});
export type InvestorBikeLink = z.infer<typeof investorBikeLinkSchema>;

export const investorDetailSchema = investorViewSchema.extend({
  links: z.array(investorBikeLinkSchema),
  /** The business's default weekly share for an investor's bike. */
  defaultWeeklyShare: moneySchema,
  collections: z.array(investorCollectionViewSchema),
  payouts: z.array(investorPayoutViewSchema),
});
export type InvestorDetail = z.infer<typeof investorDetailSchema>;

export const investorPayoutInputSchema = z.object({
  /** The vehicle partnership it settles; may be left out when the investor has only one. */
  investmentId: uuidSchema.optional(),
  amount: positiveMoneySchema,
  paidOn: isoDateSchema,
  method: z.enum(PAYMENT_METHODS),
  accountId: uuidSchema.nullish(),
  /** The bank transfer or other transaction reference. */
  reference: z.string().trim().min(1).max(120).nullish(),
  note: longTextSchema.nullish(),
  /** One per submission, so a double-click records one payout. */
  idempotencyKey: z.string().trim().min(8).max(100),
  /** Set when the person confirms a payout that looks like a duplicate is a second, real one. */
  confirmDuplicate: z.boolean().optional(),
});
export type InvestorPayoutInput = z.infer<typeof investorPayoutInputSchema>;

export const investorPayoutVoidSchema = z.object({ reason: shortTextSchema });

/** Who owns a bike from a date. Earlier charged weeks keep the owner they were charged under. */
export const bikeOwnershipChangeSchema = z
  .object({
    ownership: z.enum(BIKE_OWNERSHIPS),
    investorId: uuidSchema.nullish(),
    ownerName: shortTextSchema.nullish(),
    effectiveFrom: isoDateSchema,
    reason: shortTextSchema,
    /**
     * The investor's weekly share of this bike's remittance from the same date,
     * when it differs from the agreement already in force (or the default).
     */
    weeklyAmount: nonNegativeMoneySchema.optional(),
    /**
     * Taking a vehicle from an investor also ends their vehicle partnership's
     * cover of it from the same date (0171) — false keeps the partnership
     * earning on it, the ownership change recorded only as ownership.
     */
    endPartnershipCoverage: z.boolean().optional(),
  })
  .refine((v) => v.ownership !== 'investor' || Boolean(v.investorId), {
    path: ['investorId'],
    message: 'Choose the investor who owns it',
  })
  .refine((v) => v.ownership === 'investor' || v.weeklyAmount === undefined, {
    path: ['weeklyAmount'],
    message: 'Only an investor-owned bike has an investor share',
  });
export type BikeOwnershipChange = z.infer<typeof bikeOwnershipChangeSchema>;

/** Link one of the business's bikes to an investor, from a date. */
export const investorBikeLinkInputSchema = z.object({
  bikeId: uuidSchema,
  /** The vehicle partnership the bike is linked under; the investor's current one when left out. */
  investmentId: uuidSchema.optional(),
  /** A percentage of each week's remittance instead of a fixed weekly amount. */
  percent: z.union([z.string(), z.number()]).transform((v) => String(v).trim())
    .refine((v) => /^\d{1,3}(\.\d{1,6})?$/.test(v) && Number(v) > 0 && Number(v) <= 100, 'Between 0 and 100').optional(),
  effectiveFrom: isoDateSchema,
  /** Their weekly share from the same date; the agreement in force (or the default) when left out. */
  weeklyAmount: nonNegativeMoneySchema.optional(),
  reason: shortTextSchema,
});
export type InvestorBikeLinkInput = z.infer<typeof investorBikeLinkInputSchema>;

/** End an investor's ownership of a bike: the last day it is theirs, and who owns it after. */
export const investorBikeEndInputSchema = z.object({
  endsOn: isoDateSchema,
  nextOwnership: z.enum(['cenohub', 'rider']),
  ownerName: shortTextSchema.nullish(),
  reason: shortTextSchema,
  /** Also end the vehicle partnership's cover of this vehicle (0171). Default true. */
  endPartnershipCoverage: z.boolean().optional(),
});
export type InvestorBikeEndInput = z.infer<typeof investorBikeEndInputSchema>;

/** An agreement for an investor's weekly share of this bike's remittance, from a date. */
export const investorEntitlementInputSchema = z.object({
  weeklyAmount: nonNegativeMoneySchema,
  effectiveFrom: isoDateSchema,
  reason: shortTextSchema,
});
export type InvestorEntitlementInput = z.infer<typeof investorEntitlementInputSchema>;

/** What an ownership or entitlement change did to weeks already charged. */
export const splitChangeResultSchema = z.object({
  /** Charged weeks from the date with no payment yet: now carry the new split. */
  restampedWeeks: z.number().int(),
  /** Charged weeks from the date that already had payments: kept their split. */
  keptWeeks: z.number().int(),
});
export type SplitChangeResult = z.infer<typeof splitChangeResultSchema>;

// ── The distribution report ────────────────────────────────────────────────

export const remittanceDistributionQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  ownership: z.enum(BIKE_OWNERSHIPS).optional(),
  investorId: uuidSchema.optional(),
  riderId: uuidSchema.optional(),
  bikeId: uuidSchema.optional(),
  /** A state code from the rider's or vehicle's location. */
  state: z.string().trim().min(1).max(40).optional(),
});
export type RemittanceDistributionQuery = z.infer<typeof remittanceDistributionQuerySchema>;

export const remittanceDistributionRowSchema = z.object({
  remittanceId: uuidSchema,
  weekStartsOn: z.string(),
  weekEndsOn: z.string(),
  riderId: uuidSchema,
  riderName: z.string(),
  bikeId: uuidSchema.nullable(),
  bikeRegistration: z.string().nullable(),
  stateCode: z.string().nullable(),
  ownership: z.enum(BIKE_OWNERSHIPS).nullable(),
  investorId: uuidSchema.nullable(),
  investorName: z.string().nullable(),
  /** Charged before ownership was tracked: all company. */
  legacy: z.boolean(),
  status: z.string(),
  amountDue: moneySchema,
  collected: moneySchema,
  outstanding: moneySchema,
  investorEntitlement: moneySchema,
  investorCollected: moneySchema,
  investorOutstanding: moneySchema,
  companyEntitlement: moneySchema,
  companyCollected: moneySchema,
  companyOutstanding: moneySchema,
  /** Cleared by the owner: not owed, not paid, not revenue. */
  writtenOff: moneySchema,
  /** The bike changed riders in the week: this rider's days, of the days the share was divided over. */
  heldDays: z.number().int().nullable(),
  sharedDays: z.number().int().nullable(),
  /** Why the investor's share on this week needs a person to check it. */
  review: z.string().nullable(),
});

export const remittanceDistributionSchema = z.object({
  from: z.string(),
  to: z.string(),
  currencyCode: z.string(),
  rows: z.array(remittanceDistributionRowSchema),
  totals: z.object({
    amountDue: moneySchema,
    collected: moneySchema,
    outstanding: moneySchema,
    investorEntitlement: moneySchema,
    investorCollected: moneySchema,
    investorOutstanding: moneySchema,
    companyEntitlement: moneySchema,
    companyCollected: moneySchema,
    companyOutstanding: moneySchema,
    writtenOff: moneySchema,
  }),
  /** Cash in the period, by payment date (the basis company revenue uses). */
  cash: z.object({
    grossCollected: moneySchema,
    companyRevenue: moneySchema,
    investorCollected: moneySchema,
    investorPaidOut: moneySchema,
    /** All-time investor share collected less paid out, at the period's end. */
    investorPayable: moneySchema,
  }),
});
export type RemittanceDistribution = z.infer<typeof remittanceDistributionSchema>;

// ── One investor's weeks ───────────────────────────────────────────────────

export const investorWeeksQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
});
export type InvestorWeeksQuery = z.infer<typeof investorWeeksQuerySchema>;

export const INVESTOR_PAYOUT_STATUSES = ['paid_out', 'part_paid_out', 'not_paid_out', 'nothing_collected'] as const;
export type InvestorPayoutStatus = (typeof INVESTOR_PAYOUT_STATUSES)[number];

/**
 * An investor's weeks, each with how much of the investor's collected share
 * has been paid to them. Payouts are not tied to weeks: they settle the
 * oldest collections first.
 */
export const investorWeeksSchema = z.object({
  from: z.string(),
  to: z.string(),
  currencyCode: z.string(),
  rows: z.array(
    remittanceDistributionRowSchema.extend({
      investorPaidOut: moneySchema,
      payoutStatus: z.enum(INVESTOR_PAYOUT_STATUSES),
    }),
  ),
  totals: z.object({
    amountDue: moneySchema,
    collected: moneySchema,
    outstanding: moneySchema,
    investorEntitlement: moneySchema,
    investorCollected: moneySchema,
    investorOutstanding: moneySchema,
    investorPaidOut: moneySchema,
    companyEntitlement: moneySchema,
    companyCollected: moneySchema,
  }),
  /** Weeks in the period whose investor share needs a person to check it. */
  reviewCount: z.number().int(),
});
export type InvestorWeeks = z.infer<typeof investorWeeksSchema>;

// ── Correcting a link's dates ──────────────────────────────────────────────

/**
 * Move an investor's ownership start and/or the rider's assignment start on a
 * linked bike — usually back, to when the rider really started — and
 * recalculate every week from the earliest date affected.
 */
export const investorLinkCorrectionInputSchema = z.object({
  /** When the investor's ownership of the bike starts. */
  ownershipFrom: isoDateSchema.optional(),
  /** When the current rider's assignment of the bike starts. */
  riderStartedOn: isoDateSchema.optional(),
  /** The investor's weekly share from the ownership start. */
  weeklyAmount: nonNegativeMoneySchema.optional(),
  /** Instead of a weekly amount: a percentage of each week's remittance. */
  percent: z.union([z.string(), z.number()]).transform((v) => String(v).trim())
    .refine((v) => /^\d{1,3}(\.\d{1,6})?$/.test(v) && Number(v) > 0 && Number(v) <= 100, 'Between 0 and 100').optional(),
  /** When the new share applies from, when not from the ownership start (a change of terms). */
  shareEffectiveFrom: isoDateSchema.optional(),
  /** Also charge the rider's ended weeks on the bike that were never charged. */
  chargeUnchargedWeeks: z.boolean().optional(),
  reason: shortTextSchema,
  /** One per Save, so a double-click applies the correction once. Not needed to preview. */
  idempotencyKey: z.string().trim().min(8).max(100).optional(),
});
export type InvestorLinkCorrectionInput = z.infer<typeof investorLinkCorrectionInputSchema>;

/** The five figures kept apart, for one investor. */
export const investorFiguresSchema = z.object({
  /** Their share of weeks charged on their bikes. */
  entitled: moneySchema,
  /** Their share of what riders actually paid. */
  collected: moneySchema,
  /** Their share of charged weeks riders have not paid. */
  unpaidByRiders: moneySchema,
  /** Payouts recorded to them. */
  paidOut: moneySchema,
  /** Collected and not yet paid out. Negative: paid more than collected. */
  owed: moneySchema,
  /** Accrued partnership earnings (0171). */
  earningsDue: moneySchema,
  earningsInReview: moneySchema,
  earningsInReviewCount: z.number().int(),
  /** Earned and not yet paid out. */
  outstandingBalance: moneySchema,
});
export type InvestorFigures = z.infer<typeof investorFiguresSchema>;

export const linkCorrectionWeekSchema = z.object({
  remittanceId: uuidSchema,
  weekStartsOn: z.string(),
  weekEndsOn: z.string(),
  riderName: z.string(),
  bikeRegistration: z.string().nullable(),
  status: z.string(),
  amountDue: moneySchema,
  paid: moneySchema,
  investorEntitlementBefore: moneySchema,
  investorEntitlementAfter: moneySchema,
  investorCollectedBefore: moneySchema,
  investorCollectedAfter: moneySchema,
  companyCollectedBefore: moneySchema,
  companyCollectedAfter: moneySchema,
  heldDays: z.number().int().nullable(),
  sharedDays: z.number().int().nullable(),
  /** What the correction does to this week. */
  change: z.enum(['attached', 'charged', 'redivided', 'unchanged']),
  review: z.string().nullable(),
});

export const linkCorrectionPaymentSchema = z.object({
  paymentId: uuidSchema,
  paidOn: z.string(),
  riderName: z.string(),
  weekStartsOn: z.string(),
  amount: moneySchema,
  investorBefore: moneySchema,
  investorAfter: moneySchema,
  companyBefore: moneySchema,
  companyAfter: moneySchema,
});

export const linkCorrectionResultSchema = z.object({
  /** False for a preview: nothing was saved. */
  applied: z.boolean(),
  replayed: z.boolean(),
  /** False when the dates, share and figures already match: nothing to save. */
  changed: z.boolean(),
  batchId: uuidSchema.nullable(),
  investor: z.object({ id: uuidSchema, name: z.string() }),
  bike: z.object({ id: uuidSchema, registration: z.string() }),
  rider: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  before: z.object({ ownershipFrom: z.string(), riderStartedOn: z.string().nullable(), weeklyShare: moneySchema, sharePercent: z.string().nullable().optional() }),
  after: z.object({ ownershipFrom: z.string(), riderStartedOn: z.string().nullable(), weeklyShare: moneySchema, sharePercent: z.string().nullable().optional() }),
  recalculatedFrom: z.string(),
  weeks: z.array(linkCorrectionWeekSchema),
  payments: z.array(linkCorrectionPaymentSchema),
  /** The rider's ended weeks on the bike with no charge: charged when asked, otherwise listed. */
  unchargedWeeks: z.array(z.object({ weekStartsOn: z.string(), weekEndsOn: z.string(), amount: moneySchema, charged: z.boolean() })),
  figuresBefore: investorFiguresSchema,
  figuresAfter: investorFiguresSchema,
  /** Change in company remittance revenue: negative when investor shares are taken out of it. */
  companyRevenueAdjustment: moneySchema,
  correctionsRecorded: z.number().int(),
  flags: z.array(z.string()),
});
export type LinkCorrectionResult = z.infer<typeof linkCorrectionResultSchema>;

// ── Investor earnings (0171) ───────────────────────────────────────────────

export const INVESTOR_EARNING_STATUSES = ['accrued', 'review', 'reversed'] as const;

export const investorEarningRowSchema = z.object({
  id: uuidSchema,
  investmentId: uuidSchema,
  bikeId: uuidSchema,
  registration: z.string(),
  weekStartsOn: z.string(),
  weekEndsOn: z.string(),
  amount: moneySchema,
  status: z.enum(INVESTOR_EARNING_STATUSES),
  reviewReason: z.string().nullable(),
  /** The investor's share of what riders paid for the vehicle that week. */
  collected: moneySchema,
  /** Earned under the partnership although an ownership period gave the week to the company. */
  coveredDespiteOwnership: z.boolean(),
  confirmedAt: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalReason: z.string().nullable(),
});
export type InvestorEarningRow = z.infer<typeof investorEarningRowSchema>;

export const investorEarningConfirmSchema = z.object({ amount: positiveMoneySchema.optional(), note: shortTextSchema });
export const investorEarningReverseSchema = z.object({ reason: shortTextSchema });
